import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useDevToolsProtection } from '@/hooks/useDevToolsProtection';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { MODULES } from '@tracker/shared';
import { AuthProvider } from '@/contexts/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ProtectedRoute } from '@/components/guards/ProtectedRoute';
import { AppShell } from '@/components/layout/AppShell';
import { queryClient } from '@/lib/queryClient';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/dashboard';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { StaffListPage } from '@/pages/staff/StaffListPage';
import { StaffNewPage } from '@/pages/staff/StaffNewPage';
import { StaffDetailPage } from '@/pages/staff/StaffDetailPage';
import { StaffEditPage } from '@/pages/staff/StaffEditPage';
import { RolesListPage } from '@/pages/roles/RolesListPage';
import { RoleDetailPage } from '@/pages/roles/RoleDetailPage';
import { AccountPage } from '@/pages/account/AccountPage';
import { AccountManagePage } from '@/pages/account/AccountManagePage';
import { AccountPasswordPage } from '@/pages/account/AccountPasswordPage';
import { TrackerFdfcPage } from '@/pages/tracker-fdfc/TrackerFdfcPage';
import { TrackerFdfcEntryPage } from '@/pages/tracker-fdfc/TrackerFdfcEntryPage';

export default function App() {
  useDevToolsProtection();
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/account/me" element={<AccountPage />} />
              <Route
                path="/account/password"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_PASSWORD}>
                    <AccountPasswordPage />
                  </ProtectedRoute>
                }
              />

              {/* Manage Account — self-service profile edit */}
              <Route
                path="/account/manage"
                element={
                  <ProtectedRoute module={MODULES.ACCOUNT_MANAGE}>
                    <AccountManagePage />
                  </ProtectedRoute>
                }
              />

              {/* Staff Management — Pattern C sibling pages */}
              <Route
                path="/hrms/staff/list"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrms/staff/new"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffNewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrms/staff/:id"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hrms/staff/:id/edit"
                element={
                  <ProtectedRoute module={MODULES.HRMS_STAFF}>
                    <StaffEditPage />
                  </ProtectedRoute>
                }
              />

              {/* Roles & Permissions (System) */}
              <Route
                path="/system/roles"
                element={
                  <ProtectedRoute module={MODULES.MASTER_ROLES}>
                    <RolesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/system/roles/:code"
                element={
                  <ProtectedRoute module={MODULES.MASTER_ROLES}>
                    <RoleDetailPage />
                  </ProtectedRoute>
                }
              />

              {/* FDFC Tracker — read-only list of webhook data */}
              <Route
                path="/tracker-fdfc"
                element={
                  <ProtectedRoute module={MODULES.TRACKER_FDFC}>
                    <TrackerFdfcPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tracker-fdfc/:id"
                element={
                  <ProtectedRoute module={MODULES.TRACKER_FDFC}>
                    <TrackerFdfcEntryPage />
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster richColors position="top-right" />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
